// ==================== SMS ACCOUNT STATUS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  MessageSquare, 
  Settings,
  AlertTriangle,
  Wallet,
  Hash,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { staggerItem } from '../../../utils/animations';
import type { DashboardOverviewStats } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface SmsAccountStatusProps {
  stats: DashboardOverviewStats | null;
  isLoading?: boolean;
}

// ==================== PROVIDER DISPLAY NAMES ====================

const PROVIDER_NAMES: Record<string, string> = {
  EOCEAN: 'eOcean',
  JAZZ: 'Jazz',
  TELENOR: 'Telenor',
  UFONE: 'Ufone',
  ZONG: 'Zong',
  JEEO_SMS: 'JEEO SMS',
  OUTREACH: 'Outreach',
  SMS4CONNECT: 'SMS4Connect',
  BROADNET: 'BroadNet',
  TWILIO: 'Twilio',
  MESSAGEBIRD: 'MessageBird',
  VONAGE: 'Vonage',
  PLIVO: 'Plivo',
  SINCH: 'Sinch',
  INFOBIP: 'Infobip',
  CLICKSEND: 'ClickSend',
  TEXTLOCAL: 'Textlocal',
  MSG91: 'MSG91',
  CUSTOM: 'Custom',
};

// ==================== COMPONENT ====================

export const SmsAccountStatus = ({ stats, isLoading = false }: SmsAccountStatusProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">SMS Account Status</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              <div className="h-10 bg-(--color-border-gray) rounded" />
              <div className="h-6 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const sms = stats?.sms;
  const isConnected = sms?.connected;

  if (!isConnected) {
    return (
      <motion.div variants={staggerItem}>
        <Card className="border-yellow-500/30">
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">SMS Account Status</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="text-center py-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-2">
                <AlertTriangle size={20} className="text-yellow-400" />
              </div>
              <p className="text-sm text-white font-medium mb-0.5">Not Connected</p>
              <p className="text-xs text-(--color-muted-gray) mb-3">
                Connect your SMS provider to start sending SMS messages
              </p>
              <Link to="/app/settings/sms">
                <Button variant="primary" size="sm">
                  <Settings size={14} />
                  Connect SMS
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const providerName = sms.provider ? (PROVIDER_NAMES[sms.provider] || sms.provider) : 'Unknown';

  return (
    <motion.div variants={staggerItem}>
      <Card className="border-purple-500/20">
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">SMS Account Status</CardTitle>
            <Badge variant="success" className="gap-1">
              <CheckCircle2 size={10} />
              Connected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-2">
            {/* Provider & Sender ID */}
            <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                <MessageSquare size={14} className="text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-(--color-muted-gray)">Provider</p>
                <p className="text-xs font-medium text-white truncate">
                  {providerName}
                </p>
              </div>
            </div>

            {/* Sender ID */}
            {sms.senderId && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Hash size={14} className="text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-(--color-muted-gray)">Sender ID</p>
                  <p className="text-xs font-medium text-white truncate">
                    {sms.senderId}
                  </p>
                </div>
              </div>
            )}

            {/* Account Balance */}
            {sms.accountBalance && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Wallet size={14} className="text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-(--color-muted-gray)">Balance</p>
                  <p className="text-xs font-medium text-green-400 truncate">
                    {sms.accountBalance}
                  </p>
                </div>
              </div>
            )}

            {/* Settings Link */}
            <Link to="/app/settings/sms" className="block">
              <Button variant="secondary" size="sm" className="w-full text-xs">
                <Settings size={12} />
                Manage Settings
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SmsAccountStatus;
